import React from "react";
import {Button, Checkbox, Col, DatePicker, Form, Input, Modal, Radio, Row, Select} from "antd";
import Divider from "antd/es/divider";
import moment from "moment";

import LoadingIndicator from "components/LoadingIndicator";
import UploadWrapper from "components/UploadWrapper";
import BrdgxNationalitySelector from "components/selector/BrdgxNationalitySelector";
import BrdgxCountrySelector from "components/selector/BrdgxCountrySelector";
import OriginalIDTypeSelector from "components/selector/OriginalIDTypeSelector";
import retrieveMyinfoImg from "components/images/Primary.png";
import {
    getCurrentCorporateAccountUser,
    updateCurrentCorporateAccountUser,
    updateCurrentCorporateAccountUserMyInfo,
    callbackMyInfo,
    getCountryList,
    getIdType,
    getNationalityList,
    upload,
    deleteFile
} from "api/api";
import {displayError, displayGenericError, displaySuccessMessage} from "common/notifications";

import "components/GeneralInfoTab.css";

const EMAIL_REGEX = RegExp("[^@ ]+@[^@ ]+\\.[^@ ]+");

const MyinfoUrl = process.env.REACT_APP_MYINFO_URL;

const CRP_USER_TYPE_OPTIONS = [
    {label: "UBO", value: "UBO"},
    {label: "Shareholder", value: "Shareholder"},
    {label: "Director", value: "Director"},
    {label: "Authorized User", value: "Authorized_User"}
];

export default class CorporateAccountUserOnboardingPage extends React.Component {
    state = {
        corporateAccountUser: null,
        nationalities: [],
        countries: [],
        idTypes: [],
        loading: true,
        saving: false,
        myInfoLoading: false
    };

    componentDidMount() {
        getNationalityList()
            .then(nationalities => this.setState({nationalities}))
            .catch(displayGenericError);
        getCountryList()
            .then(countries => this.setState({countries}))
            .catch(displayGenericError);
        getIdType()
            .then(idTypes => this.setState({idTypes}))
            .catch(displayGenericError);

        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        if (code) {
            this.handleMyInfoCallback(params);
            return;
        }

        this.loadCorporateAccountUser();
    }

    loadCorporateAccountUser = () => {
        getCurrentCorporateAccountUser()
            .then(corporateAccountUser => {
                this.setState({
                    corporateAccountUser: this.bindData(corporateAccountUser),
                    loading: false
                });
            })
            .catch(error => {
                this.setState({loading: false});
                displayGenericError(error);
            });
    };

    getCookie = name => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(";").shift();
    };

    handleMyInfoCallback = params => {
        this.setState({myInfoLoading: true});
        let error = params.get("error");
        if (error === "null") {
            error = null;
        }
        let errorDescription = params.get("error_description");
        if (errorDescription === "null") {
            errorDescription = null;
        }

        callbackMyInfo(
            params.get("code"),
            params.get("state"),
            error,
            errorDescription,
            this.getCookie("code_verifier"),
            this.getCookie("oauth_state"),
            this.getCookie("oauth_nonce")
        )
            .then(myInfoResource => updateCurrentCorporateAccountUserMyInfo(myInfoResource))
            .then(corporateAccountUser => {
                this.setState({
                    corporateAccountUser: this.bindData(corporateAccountUser),
                    loading: false,
                    myInfoLoading: false
                });
                displaySuccessMessage("Your details have been retrieved from MyInfo. Please review and submit.");
            })
            .catch(error => {
                this.setState({myInfoLoading: false});
                Modal.error({
                    title: "Error",
                    content: "MyInfo is rejecting the request. Please try again or fill in your details manually."
                });
                this.loadCorporateAccountUser();
            });
    };

    handleRetrieveMyInfo = () => {
        window.open(MyinfoUrl, "_blank");
    };

    bindData = user => {
        user.documentType = "identityCard";
        const attachedFiles = user.attachedFiles || [];
        if (user.crpAccType === "INDIVIDUAL") {
            user.identityCardFront = attachedFiles.filter(f => f.attachedFileType === "IDENTITY_CARD_FRONT")[0];
            user.identityCardBack = attachedFiles.filter(f => f.attachedFileType === "IDENTITY_CARD_BACK")[0];
            user.passport = attachedFiles.filter(f => f.attachedFileType === "PASSPORT")[0];
            user.proofOfAddress = attachedFiles.filter(f => f.attachedFileType === "PROOF_OF_ADDRESS")[0];
            if (user.passport || user.proofOfAddress) {
                user.documentType = "passport";
            }
        } else {
            user.ceftificateOfIncorporation = attachedFiles
                .filter(f => f.attachedFileType === "CERTIFICATE_OF_INCORPORATION_BUSINESS_REGISTRATION")[0];
        }
        return user;
    };

    onFieldChanged = (field, val) => {
        this.setState({
            corporateAccountUser: {...this.state.corporateAccountUser, [field]: val}
        });
    };

    getCrpUserTypeValues = user => {
        const values = [];
        if (user.ubo) values.push("UBO");
        if (user.shareholder) values.push("Shareholder");
        if (user.director) values.push("Director");
        if (user.authorizedPerson) values.push("Authorized_User");
        return values;
    };

    onCrpUserTypeChanged = values => {
        this.setState({
            corporateAccountUser: {
                ...this.state.corporateAccountUser,
                ubo: values.includes("UBO"),
                shareholder: values.includes("Shareholder"),
                director: values.includes("Director"),
                authorizedPerson: values.includes("Authorized_User")
            }
        });
    };

    handleUpload = ({file, type}) => {
        const {corporateAccountUser} = this.state;
        return upload({id: corporateAccountUser.id, file, type, isCorporateUsers: true})
            .then(res => {
                const updated = {...this.state.corporateAccountUser};
                if (type === "IDENTITY_CARD_FRONT") {
                    updated.identityCardFront = res;
                } else if (type === "IDENTITY_CARD_BACK") {
                    updated.identityCardBack = res;
                } else if (type === "PASSPORT") {
                    updated.passport = res;
                } else if (type === "PROOF_OF_ADDRESS") {
                    updated.proofOfAddress = res;
                } else if (type === "CERTIFICATE_OF_INCORPORATION_BUSINESS_REGISTRATION") {
                    updated.ceftificateOfIncorporation = res;
                }
                this.setState({corporateAccountUser: updated});
            })
            .catch(displayGenericError);
    };

    handleRemoveFile = ({id, type}) => {
        deleteFile(id)
            .then(() => {
                const updated = {...this.state.corporateAccountUser};
                if (type === "IDENTITY_CARD_FRONT") {
                    updated.identityCardFront = null;
                } else if (type === "IDENTITY_CARD_BACK") {
                    updated.identityCardBack = null;
                } else if (type === "PASSPORT") {
                    updated.passport = null;
                } else if (type === "PROOF_OF_ADDRESS") {
                    updated.proofOfAddress = null;
                } else if (type === "CERTIFICATE_OF_INCORPORATION_BUSINESS_REGISTRATION") {
                    updated.ceftificateOfIncorporation = null;
                }
                this.setState({corporateAccountUser: updated});
            })
            .catch(displayGenericError);
    };

    isSubmitButtonDisabled = () => {
        const user = this.state.corporateAccountUser;
        if (!user) {
            return true;
        }
        if (user.crpAccType === "INDIVIDUAL") {
            return !(user.identityCardFront && user.identityCardBack) && !(user.passport && user.proofOfAddress);
        }
        return !user.ceftificateOfIncorporation;
    };

    handleSubmit = () => {
        const user = {...this.state.corporateAccountUser};

        if (user.email && !EMAIL_REGEX.test(user.email)) {
            displayError("Please enter a valid email address");
            return;
        }

        this.setState({saving: true});
        updateCurrentCorporateAccountUser(user)
            .then(response => {
                this.setState({
                    corporateAccountUser: this.bindData({...user, ...response}),
                    saving: false
                });
                displaySuccessMessage("Your details have been saved, It will redirect to home page in 2 seconds");
                setTimeout(() => this.props.history.push("/pages/onboarding/complete"), 2000);
            })
            .catch(error => {
                this.setState({saving: false});
                displayGenericError(error);
            });
    };

    render() {
        const {corporateAccountUser, loading, saving, myInfoLoading} = this.state;

        if (loading || myInfoLoading || !corporateAccountUser) {
            return <LoadingIndicator/>;
        }

        const user = corporateAccountUser;

        return (
            <div className="general-info-tab">
                <div className="left-pane">
                    <Row gutter={16}>
                        <Col className="gutter-row" span={12}>
                            <Form.Item label="User Type" required="true">
                                <Checkbox.Group
                                    options={CRP_USER_TYPE_OPTIONS}
                                    value={this.getCrpUserTypeValues(user)}
                                    onChange={this.onCrpUserTypeChanged}
                                />
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" span={12}>
                            <Form.Item label="Account Type" required="true">
                                <Select style={{width: "100%"}}
                                        value={user.crpAccType ? user.crpAccType : "INDIVIDUAL"}
                                        onChange={newValue => this.onFieldChanged("crpAccType", newValue)}
                                        size={"large"}>
                                    <Select.Option value="INDIVIDUAL">INDIVIDUAL</Select.Option>
                                    <Select.Option value="CORPORATE">CORPORATE</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {user.crpAccType === "INDIVIDUAL" && (
                        <div>
                            <Row gutter={16}>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="First Name" required="true">
                                        <Input size={"large"}
                                               value={user.firstName}
                                               onChange={e => this.onFieldChanged("firstName", e.target.value)}/>
                                    </Form.Item>
                                </Col>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Last Name" required="true">
                                        <Input size={"large"}
                                               value={user.lastName}
                                               onChange={e => this.onFieldChanged("lastName", e.target.value)}/>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Principal Name">
                                        <Input size={"large"}
                                               value={user.principalName}
                                               onChange={e => this.onFieldChanged("principalName", e.target.value)}/>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Nationality" required="true">
                                        <BrdgxNationalitySelector
                                            placeholder="Nationality"
                                            required="true"
                                            size={"large"}
                                            style={{width: "100%"}}
                                            value={user.nationality ? user.nationality : ""}
                                            nationalities={this.state.nationalities}
                                            onChange={newValue => this.onFieldChanged("nationality", newValue)}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Country of residency" required="true">
                                        <BrdgxCountrySelector
                                            size={"large"}
                                            placeholder="Select Country"
                                            required="true"
                                            style={{width: "100%"}}
                                            value={user.countryOfResidence}
                                            countries={this.state.countries}
                                            onChange={newValue => this.onFieldChanged("countryOfResidence", newValue)}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="ID Type">
                                        <OriginalIDTypeSelector
                                            placeholder="Id type"
                                            value={user.idType}
                                            idTypes={this.state.idTypes}
                                            onChange={newValue => this.onFieldChanged("idType", newValue)}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="ID Number" required="true">
                                        <Input value={user.idNumber}
                                               onChange={e => this.onFieldChanged("idNumber", e.target.value)}/>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Date Of Birth" required="true">
                                        <DatePicker
                                            size={"large"}
                                            style={{width: "100%"}}
                                            format={"DD/MM/YYYY"}
                                            value={user.dateOfBirth ? moment(user.dateOfBirth, "DD/MM/YYYY") : null}
                                            onChange={(newValue, newValueStr) => this.onFieldChanged("dateOfBirth", newValueStr)}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Country of birth" required="true">
                                        <Input size={"large"}
                                               value={user.countryOfBirth}
                                               onChange={e => this.onFieldChanged("countryOfBirth", e.target.value)}/>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Residential Address">
                                        <Input size={"large"}
                                               value={user.residentialAddress}
                                               onChange={e => this.onFieldChanged("residentialAddress", e.target.value)}/>
                                    </Form.Item>
                                </Col>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Postal Code">
                                        <Input size={"large"}
                                               value={user.residentialPostCode}
                                               onChange={e => this.onFieldChanged("residentialPostCode", e.target.value)}/>
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Email" required="true">
                                        <Input size={"large"}
                                               value={user.email}
                                               onChange={e => this.onFieldChanged("email", e.target.value)}/>
                                    </Form.Item>
                                </Col>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Phone">
                                        <Input size={"large"}
                                               value={user.phone}
                                               onChange={e => this.onFieldChanged("phone", e.target.value)}/>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {user.crpAccType === "CORPORATE" && (
                        <div>
                            <Row gutter={16}>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Name of Business" required="true">
                                        <Input size={"large"}
                                               value={user.corpName}
                                               onChange={e => this.onFieldChanged("corpName", e.target.value)}/>
                                    </Form.Item>
                                </Col>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Country" required="true">
                                        <BrdgxCountrySelector
                                            size={"large"}
                                            style={{width: "100%"}}
                                            placeholder="Select Country"
                                            required="true"
                                            value={user.corpCountry}
                                            countries={this.state.countries}
                                            onChange={newValue => this.onFieldChanged("corpCountry", newValue)}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Business No.">
                                        <Input size={"large"}
                                               value={user.idNumber}
                                               onChange={e => this.onFieldChanged("idNumber", e.target.value)}/>
                                    </Form.Item>
                                </Col>
                                <Col className="gutter-row" span={12}>
                                    <Form.Item label="Email" required="true">
                                        <Input size={"large"}
                                               value={user.email}
                                               onChange={e => this.onFieldChanged("email", e.target.value)}/>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    )}

                    <Divider/>

                    {user.crpAccType === "INDIVIDUAL" && (
                        <div>
                            <Form.Item label="Document Type">
                                <Radio.Group value={user.documentType}
                                             onChange={e => this.onFieldChanged("documentType", e.target.value)}>
                                    <Radio.Button value="identityCard">Identity Card</Radio.Button>
                                    <Radio.Button value="passport">Passport</Radio.Button>
                                </Radio.Group>
                            </Form.Item>

                            {user.documentType === "identityCard" && (
                                <Row gutter={16}>
                                    <Col className="gutter-row" span={12}>
                                        <UploadWrapper
                                            upload={this.handleUpload}
                                            file={user.identityCardFront}
                                            title="Identity Card (Front)"
                                            type="IDENTITY_CARD_FRONT"
                                            itemId={user.id}
                                            handleRemoveFile={this.handleRemoveFile}
                                        />
                                    </Col>
                                    <Col className="gutter-row" span={12}>
                                        <UploadWrapper
                                            upload={this.handleUpload}
                                            file={user.identityCardBack}
                                            title="Identity Card (Back)"
                                            type="IDENTITY_CARD_BACK"
                                            itemId={user.id}
                                            handleRemoveFile={this.handleRemoveFile}
                                        />
                                    </Col>
                                </Row>
                            )}

                            {user.documentType === "passport" && (
                                <Row gutter={16}>
                                    <Col className="gutter-row" span={12}>
                                        <UploadWrapper
                                            upload={this.handleUpload}
                                            file={user.passport}
                                            title="Passport"
                                            type="PASSPORT"
                                            itemId={user.id}
                                            handleRemoveFile={this.handleRemoveFile}
                                        />
                                    </Col>
                                    <Col className="gutter-row" span={12}>
                                        <UploadWrapper
                                            upload={this.handleUpload}
                                            file={user.proofOfAddress}
                                            title="Proof of Residential Address"
                                            type="PROOF_OF_ADDRESS"
                                            itemId={user.id}
                                            handleRemoveFile={this.handleRemoveFile}
                                        />
                                    </Col>
                                </Row>
                            )}
                        </div>
                    )}

                    {user.crpAccType === "CORPORATE" && (
                        <Row gutter={16}>
                            <Col className="gutter-row" span={12}>
                                <UploadWrapper
                                    upload={this.handleUpload}
                                    file={user.ceftificateOfIncorporation}
                                    title="Certificate of Incorporation / Business Registration"
                                    type="CERTIFICATE_OF_INCORPORATION_BUSINESS_REGISTRATION"
                                    itemId={user.id}
                                    handleRemoveFile={this.handleRemoveFile}
                                />
                            </Col>
                        </Row>
                    )}

                    <Row gutter={24}>
                        <Col md={24}>
                            <div className="button-wrapper">
                                {user.crpAccType === "INDIVIDUAL" && (
                                    <Button className="bt-myinfo" style={{width: 350, marginRight: 16}} onClick={this.handleRetrieveMyInfo}>
                                        <img src={retrieveMyinfoImg} alt="Retrieve MyInfo with singpass" className="bt-myinfo-img"/>
                                    </Button>
                                )}
                                <Button
                                    className="bt-next"
                                    type="primary"
                                    onClick={this.handleSubmit}
                                    loading={saving}
                                    disabled={this.isSubmitButtonDisabled() || saving}>
                                    Submit
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
        );
    }
}
